// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { CommonModule } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  Observable,
  ReplaySubject,
  Subject,
  combineLatest,
  map,
  scan,
  startWith,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { ExtendedCoilModel } from 'src/app/_interfaces/coil/coil-extension-model';
import { ApiCoilChannelHistoryModel } from 'src/app/api/remote-api/.coil.generated/models/api-coil-channel-history-model';
import { ApiCoilChannelModel } from 'src/app/api/remote-api/.coil.generated/models/api-coil-channel-model';
import { APICallGateComponent } from 'src/app/modules/shared/components/api-call-gate.component';
import { LocalizedDatePipe } from 'src/app/modules/shared/pipes/localizedDate.pipe';
import { ScreenLayoutService } from 'src/app/modules/shared/services/screen-layout-service/screen-layout.service';
import { SettingsService } from 'src/app/modules/shared/services/settings.service';
import { DeepReadonly } from 'src/app/modules/shared/utils/api-call-state.util';
import { CoilService } from '../../services/coil.service';

// delayNextCall(/channel/, 1000000, 10000);

interface Item {
  dayOfWeek: string;
  calDay: string;
  stateIconColor: string;
  coilElement: string;
}

interface FullWeekAndColor {
  coilElement: string;
}

interface CoilStateEntry {
  calDay: string;
}

interface CoilItem {
  stateIconColor: string;
}

interface NavigationExtras {
  coilName: string;
  dayOfWeek: string;
  calDay: string;
  icon: string;
  styles: string;
  coilElements: string;
  filteredEntries: string;
}

@Component({
  selector: 'app-coil-history',
  templateUrl: './coil-history.component.html',
  styleUrls: ['./coil-history.component.scss'],
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [CommonModule, APICallGateComponent, LocalizedDatePipe],
})
export class CoilHistoryComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) coil!: ExtendedCoilModel;

  // creates our own APICall instance (not shared with anyone)
  // could use MapApiService directly as well...
  initialChannelLength = 4;
  last14DaysCalDays$!: Observable<string[]>;
  showExpandedChannels = false;
  coilChannelHistoryEndpoint!: ReturnType<
    CoilService['mapCoilChannelHistoryEndpoint']
  >;
  coilChannelHistory$ = new ReplaySubject<
    DeepReadonly<ApiCoilChannelModel[]>
  >();

  upperMoreChannelsArrowIcon = true;
  lowerMoreChannelsArrowIcon = true;
  showCompleteFirstWeekChannel = false;
  showCompleteSecondWeekChannel = false;
  initialPhoneArrowIcon = 'arrow-line-right-s';
  expandedPhoneArrowIcon = 'arrow-line-down-s';
  phoneArrowIcons: string[] = [];

  smallGreyCircleSize = '11px';

  clickUpperChannels$ = new ReplaySubject<void>();
  clickLowerChannels$ = new ReplaySubject<void>();

  protected coilHistoryEndpoint!: ReturnType<
    CoilService['mapCoilHistoryEndpoint']
  >;
  private coilMarkers$ = this.coilService.getCoilMarkers.lastSuccessfulData$;

  @Output() ready = new EventEmitter<void>();

  invertBiggerCirclesTimelineSubject$ = this.settingsService.timelineAscending$;
  invertSmallerCirclesTimelineSubject$ =
    this.settingsService.timelineAscending$;

  private destroy$ = new Subject<void>();

  coilHistory$ = new ReplaySubject<
    DeepReadonly<ApiCoilChannelHistoryModel[]>
  >();
  // coilHistory$.next(coilHistory);

  constructor(
    protected coilService: CoilService,
    public screenLayoutService: ScreenLayoutService,
    private settingsService: SettingsService,
    private router: Router
  ) {}

  ngOnChanges(): void {
    // whenever @Input coil changes this method is invoked
    // in that case we need to do a new request
    this.getCoilHistoryEndpoint();
    this.getCoilChannelHistoryEndpoint();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleGrid(): void {
    this.showExpandedChannels = !this.showExpandedChannels;
    if (this.showExpandedChannels) {
      this.requestCoilChannelHistory();
    }
  }

  togglePhoneGrid(): void {
    this.requestCoilChannelHistory();
  }

  handleEnterKey(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.toggleGrid();
    }
  }

  toggleChannelsText(position: 'upper' | 'lower'): void {
    if (position === 'upper') {
      this.clickUpperChannels$.next();
      this.upperMoreChannelsArrowIcon = !this.upperMoreChannelsArrowIcon;
    } else if (position === 'lower') {
      this.clickLowerChannels$.next();
      this.lowerMoreChannelsArrowIcon = !this.lowerMoreChannelsArrowIcon;
    }
  }

  toggleWeekChannelData(
    showCompleteWeekChannel: boolean,
    coilDataWithColorsWeek$: Observable<ApiCoilChannelModel[]>
  ): Observable<ApiCoilChannelModel[]> {
    const newObservable = coilDataWithColorsWeek$.pipe(
      map((data) => (showCompleteWeekChannel ? data : data.slice(0, 4)))
    );
    showCompleteWeekChannel = !showCompleteWeekChannel;
    return newObservable;
  }

  toggleFirstWeekChannelData(): void {
    this.sliceFirstWeekChannel$ = this.toggleWeekChannelData(
      this.showCompleteFirstWeekChannel,
      this.coilDataWithColorsFirstWeek$
    );
    this.showCompleteFirstWeekChannel = !this.showCompleteFirstWeekChannel;
  }

  toggleSecondWeekChannelData(): void {
    this.sliceSecondWeekChannel$ = this.toggleWeekChannelData(
      this.showCompleteSecondWeekChannel,
      this.coilDataWithColorsSecondWeek$
    );
    this.showCompleteSecondWeekChannel = !this.showCompleteSecondWeekChannel;
  }

  toggleUpperChannelText$ = this.clickUpperChannels$.pipe(
    startWith(false),
    scan((acc) => !acc)
  );

  toggleLowerChannelText$ = this.clickLowerChannels$.pipe(
    startWith(false),
    scan((acc) => !acc)
  );

  upperChannelText$ = this.createChannelTextStream(
    this.coilChannelHistory$ as unknown as Observable<
      ApiCoilChannelHistoryModel[]
    >,
    this.toggleUpperChannelText$ as unknown as Observable<boolean>
  );
  lowerChannelText$ = this.createChannelTextStream(
    this.coilChannelHistory$ as unknown as Observable<
      ApiCoilChannelHistoryModel[]
    >,
    this.toggleLowerChannelText$ as unknown as Observable<boolean>
  );

  coilHistoryDateInfo$ = this.coilHistory$.pipe(
    map((coilHistory) =>
      coilHistory.map((historyItem) => {
        const [year, month, day] = historyItem.calDay.split('-');
        const date = new Date(+year, +month - 1, +day);
        const dayOfWeek = date.toLocaleDateString('en-US', {
          weekday: 'short',
        });
        return { ...historyItem, calDayOnly: day, dayOfWeek };
      })
    )
  );

  // Sets the colors for the icons
  biggerCirclesGenerator$ = combineLatest([
    this.coilHistoryDateInfo$,
    this.coilMarkers$,
  ]).pipe(
    map(([coilHistory, coilMarkers]) =>
      coilHistory.map((historyItem) => ({
        ...historyItem,
        stateIconColor: coilMarkers.find(
          (marker) => marker.coilOkStatus === historyItem.coilStatus
        )?.stateIconColor,
      }))
    )
  );

  sortedCoilDataByDayAscending$ = this.biggerCirclesGenerator$.pipe(
    map((coilDataWithColor) =>
      coilDataWithColor.sort((firstCoil, secondCoil) =>
        secondCoil.calDay.localeCompare(firstCoil.calDay)
      )
    )
  );

  coilsSortedBySettings$ = this.invertBiggerCirclesTimelineSubject$.pipe(
    switchMap((ascending) =>
      ascending
        ? this.sortedCoilDataByDayAscending$.pipe(
            map((data) => [...data].reverse())
          )
        : this.sortedCoilDataByDayAscending$
    )
  );

  fullWeek$ = this.coilsSortedBySettings$.pipe(
    map((coilDataWithColor) => coilDataWithColor)
  );

  firstWeek$ = this.coilsSortedBySettings$.pipe(
    map((coilDataWithColor) => coilDataWithColor.slice(0, 7))
  );

  secondWeek$ = this.coilsSortedBySettings$.pipe(
    map((coilDataWithColor) => coilDataWithColor.slice(7, 14))
  );

  smallerCirclesGenerator$ = combineLatest([
    this.coilChannelHistory$,
    this.coilMarkers$,
  ]).pipe(
    map(([coilChannelHistory, coilMarkers]) =>
      coilChannelHistory.map((history) => ({
        ...history,
        coilChannelHistory: history.coilChannelHistory.map((channel) => ({
          ...channel,
          stateIconColor: coilMarkers.find(
            (marker) => marker.coilOkStatus === channel.coilStatus
          )?.stateIconColor,
        })),
      }))
    )
  );

  invertSmallerCirclesTimeLine$ = this.smallerCirclesGenerator$.pipe(
    map((calDay) =>
      calDay.map((coilElement) => ({
        ...coilElement,
        coilChannelHistory: coilElement.coilChannelHistory.sort(
          (first, second) => second.calDay.localeCompare(first.calDay)
        ),
      }))
    )
  );

  coilStateEntriesByDate$ = this.invertSmallerCirclesTimeLine$.pipe(
    map((coilData) => {
      const result: { [key: string]: string[] } = {};

      coilData.forEach((coil) => {
        coil.coilChannelHistory.forEach(({ calDay, stateIconColor }) => {
          if (!result[calDay]) {
            result[calDay] = [];
          }
          result[calDay].push(`${coil.coilElement} - ${stateIconColor}`);
        });
      });

      return Object.entries(result).map(([calDay, entries]) => ({
        calDay,
        entries,
      }));
    })
  );

  sortSmallerCirclesByDate$ = this.smallerCirclesGenerator$.pipe(
    map((stateIconColor) =>
      stateIconColor.map((coilElement) => ({
        ...coilElement,
        coilChannelHistory: coilElement.coilChannelHistory.sort(
          (first, second) => first.calDay.localeCompare(second.calDay)
        ),
      }))
    )
  );

  coilDataWithColorsWeekSliced$ = (
    startSlice: number,
    endSlice: number
  ): Observable<ApiCoilChannelModel[]> => {
    return this.invertSmallerCirclesTimelineSubject$.pipe(
      switchMap((invertedTimeline) =>
        invertedTimeline
          ? this.sortSmallerCirclesByDate$
          : this.invertSmallerCirclesTimeLine$
      ),
      map((coilDataWithColors) => {
        return coilDataWithColors.map((coilData) => ({
          ...coilData,
          coilChannelHistory: coilData.coilChannelHistory.slice(
            startSlice,
            endSlice
          ),
          coiStatus: coilData.coilChannelHistory.filter(
            (history) => history.coilStatus === 0
          ).length,
        }));
      }),
      map((sortedCoilDataWithColors) =>
        sortedCoilDataWithColors.sort(
          (coilDataA, coilDataB) => coilDataB.coiStatus - coilDataA.coiStatus
        )
      )
    );
  };

  coilDataWithColorsWeekSlicedUnsliced$ = (): Observable<
    ApiCoilChannelModel[]
  > => {
    return this.invertSmallerCirclesTimelineSubject$.pipe(
      switchMap((invertedTimeline) =>
        invertedTimeline
          ? this.sortSmallerCirclesByDate$
          : this.invertSmallerCirclesTimeLine$
      ),
      map((coilDataWithColors) => {
        return coilDataWithColors.map((coilData) => ({
          ...coilData,
          coilChannelHistory: coilData.coilChannelHistory,
          coiStatus: coilData.coilChannelHistory.filter(
            (history) => history.coilStatus === 0
          ).length,
        }));
      }),
      map((sortedCoilDataWithColors) =>
        sortedCoilDataWithColors.sort(
          (coilDataA, coilDataB) => coilDataB.coiStatus - coilDataA.coiStatus
        )
      )
    );
  };

  coilDataWithColorsFullWeek$ = this.coilDataWithColorsWeekSlicedUnsliced$();

  fullWeekAndColors$ = combineLatest([
    this.fullWeek$,
    this.coilDataWithColorsFullWeek$,
  ]).pipe(
    map(([fullWeek, coilDataWithColorsFullWeek]) => {
      return fullWeek.map((item, index) => ({
        ...item,
        coilElement:
          coilDataWithColorsFullWeek[index]?.coilElement || 'default value',
      }));
    })
  );

  coilDataWithColorsFirstWeek$ = this.coilDataWithColorsWeekSliced$(0, 7);

  coilDataWithColorsSecondWeek$ = this.coilDataWithColorsWeekSliced$(7, 14);

  sliceFirstWeekChannel$: Observable<ApiCoilChannelModel[]> =
    this.coilDataWithColorsFirstWeek$.pipe(map((data) => data.slice(0, 4)));

  sliceSecondWeekChannel$: Observable<ApiCoilChannelModel[]> =
    this.coilDataWithColorsSecondWeek$.pipe(map((data) => data.slice(0, 4)));

  countCoilStatusZero$ = this.coilChannelHistory$.pipe(
    map((data: ReadonlyArray<DeepReadonly<ApiCoilChannelModel>>) =>
      this.countCoilStatusZero(data)
    )
  );

  statusCount$ = this.coilChannelHistory$.pipe(
    map((data: ReadonlyArray<DeepReadonly<ApiCoilChannelModel>>) =>
      this.countCoilStatusZero(data)
    )
  );

  fullWeekWithStatusCount$ = combineLatest([
    this.fullWeekAndColors$,
    this.statusCount$,
  ]).pipe(
    map(([fullWeekAndColors, statusCount]) =>
      fullWeekAndColors.map((item) => ({
        ...item,
        statusCount: statusCount[item.calDay] || 0,
      }))
    )
  );

  setDateIcon(stateIconColor: string): string {
    switch (stateIconColor) {
      case 'rgba(var(--functional-red), var(--opacity-1))':
        return 'error';
      case 'rgba(var(--functional-green), var(--opacity-1))':
        return 'success';
      case 'var(--base-1)':
        return '';
      default:
        return '';
    }
  }

  resizeGreyCircle(
    color: { stateIconColor: string },
    size: string
  ): { [key: string]: string } {
    if (color.stateIconColor === 'var(--base-1)') {
      const hasIcon = !!this.setDateIcon(color.stateIconColor);
      return {
        'background-color': color.stateIconColor,
        width: hasIcon ? '' : size,
        height: hasIcon ? '' : size,
      };
    }
    return {};
  }

  resizeGreyCircleDefault(color: { stateIconColor: string }): {
    [key: string]: string;
  } {
    return this.resizeGreyCircle(color, this.smallGreyCircleSize);
  }

  resizeGreyCirclePhone(color: { stateIconColor: string }): {
    [key: string]: string;
  } {
    const resizedCircle = this.resizeGreyCircle(color, '1rem');

    return {
      ...resizedCircle,
      'margin-top': '1rem',
      'margin-bottom': '1rem',
    };
  }

  setGreyCircleColor(item: CoilItem): { [key: string]: string } {
    return {
      'background-color': item.stateIconColor,
      color:
        this.setDateIcon(item.stateIconColor) === '' ? '--opacity-3' : 'white',
    };
  }

  setFilteredEntries(item: Item): void {
    const icon = this.setDateIcon(item.stateIconColor);
    const styles = this.resizeGreyCirclePhone(item);

    this.fullWeekAndColors$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((fullWeekAndColors: FullWeekAndColor[]) => {
        const coilElements = fullWeekAndColors.map(
          (weekItem) => weekItem.coilElement
        );

        this.filterCoilStateEntriesByDate(item.calDay)
          .pipe(takeUntil(this.destroy$))
          .subscribe((filteredEntries: CoilStateEntry[]) => {
            this.navigateToCoilPhone(
              this.coil.coilName || '',
              item.dayOfWeek,
              item.calDay,
              icon,
              styles,
              coilElements,
              filteredEntries
            );
          });
      });

    this.storeExpandedCoil();
    this.storeScrollPosition();
  }

  navigateToCoilPhone(
    coilName: string,
    dayOfWeek: string,
    calDay: string,
    icon: string,
    styles: { [key: string]: string },
    coilElements: string[],
    filteredEntries: CoilStateEntry[]
  ): void {
    const navigationExtras = this.createNavigationExtras(
      coilName,
      dayOfWeek,
      calDay,
      icon,
      styles,
      coilElements,
      filteredEntries
    );

    this.router.navigate([
      '/system',
      this.coil.systemMaterialNumber,
      this.coil.systemSerialNumber,
      'coil-phone-view',
      navigationExtras,
    ]);
  }

  private storeExpandedCoil(): void {
    sessionStorage.setItem(
      'expandedCoilSerialNumber',
      this.coil.coilSerialNumber
    );
  }

  private storeScrollPosition(): void {
    sessionStorage.setItem('scrollPosition', window.scrollY.toString());
  }

  private createChannelTextStream(
    coilChannelHistory$: Observable<ApiCoilChannelHistoryModel[]>,
    toggleChannelText$: Observable<boolean>
  ): Observable<string> {
    return combineLatest([coilChannelHistory$, toggleChannelText$]).pipe(
      map(([channelHistoryLength, toggleChannel]) =>
        toggleChannel
          ? 'Show Less'
          : channelHistoryLength.length <= 5
          ? 'More Channel'
          : 'More Channels'
      )
    );
  }

  private filterCoilStateEntriesByDate(calDay: string) {
    return this.coilStateEntriesByDate$.pipe(
      map((entries: CoilStateEntry[]) =>
        entries.filter((entry) => entry.calDay === calDay)
      )
    );
  }

  private countCoilStatusZero(
    data: ReadonlyArray<DeepReadonly<ApiCoilChannelModel>>
  ): Record<string, number> {
    return data.reduce((statusCount, channel) => {
      this.updateStatusCount(statusCount, channel);
      return statusCount;
    }, {} as Record<string, number>);
  }

  private updateStatusCount(
    statusCount: Record<string, number>,
    channel: DeepReadonly<ApiCoilChannelModel>
  ): void {
    channel.coilChannelHistory.forEach((history) => {
      if (history.coilStatus === 0) {
        statusCount[history.calDay] = (statusCount[history.calDay] || 0) + 1;
      }
    });
  }

  private createNavigationExtras(
    coilName: string,
    dayOfWeek: string,
    calDay: string,
    icon: string,
    styles: { [key: string]: string },
    coilElements: string[],
    filteredEntries: CoilStateEntry[]
  ): NavigationExtras {
    return {
      coilName,
      dayOfWeek,
      calDay,
      icon,
      styles: JSON.stringify(styles),
      coilElements: JSON.stringify(coilElements),
      filteredEntries: JSON.stringify(filteredEntries),
    };
  }

  private requestCoilChannelHistory(): void {
    const params = {
      coilSerialNumber: +this.coil.coilSerialNumber,
      coilMaterialNumber: this.coil.coilMaterialNumber,
      systemSerialNumber: this.coil.systemSerialNumber,
      systemMaterialNumber: this.coil.systemMaterialNumber,
    };
    if (this.coilChannelHistoryEndpoint) {
      this.coilChannelHistoryEndpoint.request(params);
    } else {
      console.log('this.coilChannelHistoryEndpoint is not defined');
    }
  }

  private getCoilHistoryEndpoint(): void {
    const params = {
      coilSerialNumber: +this.coil.coilSerialNumber,
      coilMaterialNumber: this.coil.coilMaterialNumber,
      systemSerialNumber: this.coil.systemSerialNumber,
      systemMaterialNumber: this.coil.systemMaterialNumber,
    };
    // generate our own private endpoint here
    this.coilHistoryEndpoint = this.coilService.mapCoilHistoryEndpoint(params);
    this.coilHistoryEndpoint.request(params);
    // explict subscription unfortunately
    this.coilHistoryEndpoint.lastSuccessfulData$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.coilHistory$.next(data);
      });
  }

  private getCoilChannelHistoryEndpoint(): void {
    const params = {
      coilSerialNumber: +this.coil.coilSerialNumber,
      coilMaterialNumber: this.coil.coilMaterialNumber,
      systemSerialNumber: this.coil.systemSerialNumber,
      systemMaterialNumber: this.coil.systemMaterialNumber,
    };
    // generate our own private endpoint here
    this.coilChannelHistoryEndpoint =
      this.coilService.mapCoilChannelHistoryEndpoint(params);
    // DO NOT request this yet (only when user expands!)
    // this.coilChannelHistoryEndpoint.request(params);
    // explict subscription unfortunately
    this.coilChannelHistoryEndpoint.lastSuccessfulData$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.coilChannelHistory$.next(data);
      });
  }
}
